// Realce de sintaxe em uma passada só — suficiente para os blocos de código da
// documentação (JSON, curl/bash, JS, Python, PHP).
//
// Não usamos Shiki/Prism de propósito: a paleta .tok-* já existe no styles.css e
// está calibrada para a identidade visual do Infotravel. Trocar por um
// highlighter de mercado significaria adotar o tema dele ou reescrever o tema
// inteiro em CSS — mais dependência para um resultado menos alinhado.

import { isValidElement, type ReactNode } from "react";

const escapeHtml = (s: string): string => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const KEYWORDS =
  /\b(GET|POST|PUT|DELETE|PATCH|curl|import|from|export|const|let|var|function|async|await|return|if|else|def|require|new|class|extends|True|False|None|true|false|null|CURLOPT_CUSTOMREQUEST|CURLOPT_HTTPHEADER|CURLOPT_POSTFIELDS|CURLOPT_RETURNTRANSFER|curl_init|curl_setopt_array|curl_exec|json_decode|json_encode|fetch|requests)\b/g;

const TOKEN =
  /(#[^\n]*|(?:^|\s)\/\/[^\n]*)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`)|(-?\b\d+(?:\.\d+)?\b)|(\$\w+)/g;

// Extrai texto puro de children arbitrários do React (strings, arrays,
// elementos) para nunca acabar chamando String() em um objeto e imprimir
// "[object Object]" dentro de um bloco de código.
export function childrenToText(node: ReactNode): string {
  if (node === null || node === undefined || node === false || node === true) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(childrenToText).join("");
  // Um elemento do React: desce para os filhos dele. `isValidElement` é o que
  // estreita o tipo — ler `.props` num ReactNode qualquer não compila, e é a
  // mesma checagem que o código já fazia à mão.
  if (isValidElement<{ children?: ReactNode }>(node)) return childrenToText(node.props.children);
  return "";
}

export function highlightCode(source: ReactNode): string {
  const src = typeof source === "string" ? source : childrenToText(source);
  if (!src) return "";

  let out = "";
  let last = 0;
  let m: RegExpExecArray | null;
  TOKEN.lastIndex = 0;

  // biome-ignore lint/suspicious/noAssignInExpressions: laço com exec() é assim que se escreve
  while ((m = TOKEN.exec(src))) {
    out += escapeHtml(src.slice(last, m.index)).replace(KEYWORDS, '<span class="tok-k">$1</span>');
    const [full, comment, str, num, variable] = m;
    if (comment) {
      out += `<span class="tok-c">${escapeHtml(comment)}</span>`;
    } else if (str) {
      // Uma string seguida de ":" é chave de objeto JSON, não valor — cor
      // diferente, senão um payload inteiro vira um bloco monocromático.
      const isKey = /^\s*:/.test(src.slice(m.index + full.length));
      out += `<span class="${isKey ? "tok-p" : "tok-s"}">${escapeHtml(str)}</span>`;
    } else if (num) {
      out += `<span class="tok-n">${escapeHtml(num)}</span>`;
    } else if (variable) {
      out += `<span class="tok-m">${escapeHtml(variable)}</span>`;
    }
    last = m.index + full.length;
  }

  out += escapeHtml(src.slice(last)).replace(KEYWORDS, '<span class="tok-k">$1</span>');
  return out;
}
